// src/private/src/router/index.jsx
// 
import React from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';

import { AuthLayout, AppLayout, ProviderComposer } from '@src/components';
import { I18nProvider } from '@src/components/core/i18n-provider';
import { LocalizationProvider } from '@src/components/core/localization-provider';
import { ScrollRestoration } from '@src/components/core/scroll-restoration';
import { Toaster } from '@src/components/core/toaster';
import config from '@src/config';
import paths from '@src/paths';
import { VerifyContainer } from '@src/containers';
import { AuthProvider, MessageProvider, SidebarProvider } from '@src/context/common';
import { useAuth } from '@src/context';
import {
  Login,
  Page404,
  Page500,
  ErrorBoundary,
  DailyPulse,
  DataHealthPage,
  DataBackfillPage,
  PerformanceTrends,
  ClientPerformance,
  CompanyPerformance,
  GenderPerformancePage,
  ClientPerformanceAggregator,
  DesignerCreativePerformance,
  AdPerformancePage,
  DashboardOverviewPage,
  BackendDataListPage,
  BackendDataSummaryPage,
  TaskBoardPage,
  ClientInboxPage,
  NotificationsPage,
  KnowledgeBasePage,
  CreativeStudioPage,
  ConceptSystemPage,
  PublicInvoicePage,
  OnboardingFlowPage,
  OnboardingWizardPage,
  ParentOnboardingWizardPage,
  PublicProjectionsPage,
  PublicProjectionsClientView,
  GetStartedPage,
  GetStartedFiveStarPage,
  FiveStarOnboardingWizardPage,
  ContractSignPage,
  EmailMappingBuilderPage,
  TaskLoggerPage,
  AccountPage,
  APIAccessPage,
  PasswordReset,
  AccountSetup,
  AdNameBuilderPage,
  CampaignBuilderPage,
  BudgetOptimizerPage,
} from '@src/pages';

import { DailyPulseProvider, ClientPerformanceProvider, OverviewProvider  } from '@src/context';
import { ClientListPage, ClientDetailPage, ClientZipsPage, ClientDashboardPage, ClientBudgetsPage, BudgetCenterPage, BudgetSubmissionsPage, ClientLanesPage, ClientOffersPage, ClientLandingPagesPage, ClientCreativeKnowledgePage, ClientHubPage, OnboardingListPage, OnboardingWorkflowPage, ClientMemberInvitePage, ZipCoveragePage, ZipSubmissionsPage, ZipTargetingAnalyzerPage, CreativeTasksPage, DataIntegrationHealthPage, DataIntegrationHistoryPage, DataIntegrationWizardPage, TeamManagementPage, ProspectListPage, ClientCreatePage, OnboardingChecklistPage, OnboardingDashboardPage, WebhookSetupPage, FiveStarEmailNotificationsPage, PacingControlPage, ActiveHoldsAdminPage, ClientBillingThresholdPage, BillingFleetPage, BillingActivityPage, ClientSchedulePage, StaffMeetingRequestsPage } from '@src/pages/app/clients';
import ProspectFitReportPage from '@src/pages/app/clients/ProspectFitReportPage';
import { InvoiceListPage, InvoiceDetailPage, InvoiceFormPage, InvoiceSettingsPage } from '@src/pages/app/invoices';
import { ClientInvoiceListPage, ClientInvoiceDetailPage } from '@src/pages/app/client-invoices';
import {
  MyBillingPage,
  MyBillingActivityPage,
  MyStatementsPage,
} from '@src/pages/app/client-billing';
import { LeadListPage } from '@src/pages/app/leads';
import LeadDetailPage from '@src/pages/app/leads/LeadDetailPage'; 
import FlowHubPage from '@src/pages/app/flows/FlowHubPage';
import ZipCodeUpdatesFlowPage from '@src/pages/app/flows/ZipCodeUpdatesFlowPage';
import { AgentChatPage } from '@src/pages/app/agent-chat';
import { AgentMonitorPage } from '@src/pages/app/agent-monitor';









import Protect from './Protect';

const PROVIDERS = [
  LocalizationProvider,
  MessageProvider,
  AuthProvider,
  [I18nProvider, { language: 'en' }],
  SidebarProvider,
];

function normalizeSlug(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

function getMembershipDashboardSlug(membership = {}) {
  const clientSlug = membership?.client_slug || membership?.client_id;
  if (clientSlug) {
    return clientSlug;
  }
  // Prefer the authoritative parent-company slug the backend resolves
  // against; fall back to a name-derived slug only when it's absent.
  const parentSlug = membership?.parent_company_slug;
  if (parentSlug) {
    return parentSlug;
  }
  const parentName = membership?.parent_company_name;
  if (parentName) {
    return normalizeSlug(parentName);
  }
  return membership?.parent_company_id || null;
}

function RootContainer() {
  return (
    <ErrorBoundary>
      <Helmet>
        <title>{config.SITE.title}</title>
      </Helmet>
      <ProviderComposer providers={PROVIDERS}>
        <ScrollRestoration/>
        <VerifyContainer>
          <ClientMemberRouteGuard>
            <Outlet/>
          </ClientMemberRouteGuard>
          <Toaster position="bottom-right"/>
        </VerifyContainer>
      </ProviderComposer>
    </ErrorBoundary>
  );
}

function ClientMemberRouteGuard({ children }) {
  const { state } = useAuth();
  const location = useLocation();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;

  // Creative designers: redirect to /creative-tasks if they land on pages they can't see
  if (user?.is_creative_designer && !user?.is_staff && !user?.is_superuser) {
    const path = location.pathname.replace(/\/+$/, '');
    const allowedPaths = ['/creative-tasks', '/clients/creative-knowledge', '/clients/lanes'];
    if (allowedPaths.some((p) => path.startsWith(p))) {
      return children;
    }
    return <Navigate to="/creative-tasks" replace />;
  }

  if (!isClientMember) {
    return children;
  }

  const parentScopeMembership = memberships.find(
    (m) => !m?.client_id && (m?.parent_company_id || m?.parent_company_name)
  );
  const primary = parentScopeMembership || memberships[0] || {};
  const slug = getMembershipDashboardSlug(primary);
  const isParentScopeMembership = Boolean(parentScopeMembership);
  const parentSlug = primary?.parent_company_slug;
  // For parent company memberships, use first child's status or default to onboarding
  const status = isParentScopeMembership
    ? (primary?.client_status || 'onboarding').toLowerCase()
    : String(primary?.client_status || '').toLowerCase();
  const path = location.pathname.replace(/\/+$/, '');
  const onboardingPath = isParentScopeMembership && parentSlug
    ? `/onboarding/parent/${parentSlug}`
    : slug ? `/onboarding/${slug}` : null;
  const dashboardPath = slug ? `/clients/${slug}/dashboard` : null;
  const anyClientDashboardPath = /^\/clients\/[^/]+\/dashboard$/.test(path);
  const isLeadPath = path === '/leads' || /^\/leads\/[^/]+$/.test(path);
  const isBudgetCenterPath = path === '/budget-center';
  const isPublicInvoicePath = /^\/invoices\/[^/]+$/.test(path);
  const anyClientSchedulePath = /^\/clients\/[^/]+\/schedule$/.test(path);
  const schedulePath = slug ? `/clients/${slug}/schedule` : null;
  const accountPath = '/settings/account';
  const apiAccessPath = '/settings/api-access';
  const flowPaths = new Set(['/flows', '/flows/zip-code-updates']);
  const onboardingStatuses = new Set(['new_business', 'onboarding']);

  if (flowPaths.has(path)) {
    return children;
  }

  if (isParentScopeMembership && anyClientDashboardPath) {
    return children;
  }

  if (isParentScopeMembership && anyClientSchedulePath) {
    return children;
  }

  if (schedulePath && path === schedulePath) {
    return children;
  }

  if (isLeadPath) {
    return children;
  }

  if (isBudgetCenterPath) {
    return children;
  }

  if (path === '/my-invoices' || path.startsWith('/my-invoices/')) {
    return children;
  }

  if (
    path === '/my-billing' ||
    path === '/my-billing/activity' ||
    path === '/my-billing/statements'
  ) {
    return children;
  }

  if (path === '/team') {
    return children;
  }

  if (path === '/zip-coverage') {
    return children;
  }

  if (path === '/guru') {
    return children;
  }

  if (path === '/pacing-control') {
    return children;
  }

  // Creative Performance (/ad-performance) — beta rollout to client members,
  // gated per-client by the ad_performance_enabled flag on their membership.
  // Unflagged clients fall through and are bounced to their dashboard.
  if (
    path === '/ad-performance' &&
    (isParentScopeMembership || memberships.some((m) => m?.ad_performance_enabled))
  ) {
    return children;
  }

  if (path.startsWith('/data-integrations')) {
    return children;
  }

  if (/^\/clients\/[^/]+\/webhook-setup$/.test(path) || path === '/lead-delivery') {
    return children;
  }

  if (path === '/email-notifications') {
    return children;
  }

  if (isPublicInvoicePath) {
    return children;
  }

  // Allow parent company members to access any child client's onboarding page
  if (path.startsWith('/onboarding/')) {
    return children;
  }

  if (path === '/get-started' || path.startsWith('/get-started/')) {
    return children;
  }

  if (onboardingStatuses.has(status)) {
    if (onboardingPath && path === onboardingPath) {
      return children;
    }
    if (onboardingPath) {
      return <Navigate to={onboardingPath} replace />;
    }
    return <Navigate to={paths.home} replace />;
  }

  if (path === accountPath || path === apiAccessPath || (dashboardPath && path === dashboardPath)) {
    return children;
  }

  if (dashboardPath) {
    return <Navigate to={dashboardPath} replace />;
  }
  return <Navigate to={accountPath} replace />;
}

function AppContainer() {
  return (
    <Protect.Authenticated>
      <AppLayout>
        <Outlet/>
      </AppLayout>
    </Protect.Authenticated>
  );
}

function AuthContainer() {
  return (
    <Protect.Any>
      <AuthLayout>
        <Outlet />
      </AuthLayout>
    </Protect.Any>
  );
}

function StaffOnly({ children }) {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;
  if (isStaff) {
    return children;
  }
  // Creative designers get through to their limited pages
  if (user?.is_creative_designer) {
    return children;
  }
  const memberships = user?.client_memberships || [];
  const isClientMember = !user?.is_staff && !user?.is_superuser && memberships.length > 0;
  if (isClientMember) {
    const primary = memberships[0] || {};
    const dashboardSlug = getMembershipDashboardSlug(primary);
    if (dashboardSlug) {
      return <Navigate to={paths.clientDashboard(dashboardSlug)} replace />;
    }
  }
  return <Navigate to={paths.clients} replace />;
}

// Creative Performance (/ad-performance) is staff-only by default but is
// being rolled out to client members in beta, gated per-client by the
// `ad_performance_enabled` flag on their membership. Staff always pass;
// client members pass only when at least one of their memberships is opted
// in; everyone else is redirected exactly like StaffOnly.
function StaffOrAdPerformanceClient({ children }) {
  const { state } = useAuth();
  const user = state?.user;
  const isStaff = user?.is_staff || user?.is_superuser;
  if (isStaff) {
    return children;
  }
  const memberships = user?.client_memberships || [];
  const isClientMember = memberships.length > 0;
  const adPerformanceEnabled = memberships.some((m) => m?.ad_performance_enabled);
  // Parent-company members carry no per-client flag; the page defaults to all
  // their children and the backend filters to ad_performance_enabled clients.
  const isParentScopeMember = memberships.some(
    (m) => !m?.client_id && (m?.parent_company_id || m?.parent_company_name),
  );
  if (isClientMember && (adPerformanceEnabled || isParentScopeMember)) {
    return children;
  }
  if (isClientMember) {
    const primary = memberships[0] || {};
    const dashboardSlug = getMembershipDashboardSlug(primary);
    if (dashboardSlug) {
      return <Navigate to={paths.clientDashboard(dashboardSlug)} replace />;
    }
  }
  return <Navigate to={paths.clients} replace />;
}

// https://reactrouter.com/en/main/start/overview
const router = createBrowserRouter([
  {
    element: <RootContainer />,
    children: [
      {
        path: '/',
        element: <AuthContainer />,
        children: [
          {
            path: '',
            element: <Login />,
          },
          {
            path: 'auth/login',
            element: <Login />,
          },
          {
            path: 'auth/cognito/reset-password',
            element: <PasswordReset />,
          },
          {
            path: 'auth/setup-account',
            element: <AccountSetup />,
          },
        ],
      },
      {
        path: 'projections/:clientSlug/client-view',
        element: <Protect.Any><PublicProjectionsClientView /></Protect.Any>,
      },
      {
        path: 'get-started',
        element: <Protect.Any><GetStartedPage /></Protect.Any>,
      },
      {
        path: 'get-started/five-star',
        element: <Protect.Any><GetStartedFiveStarPage /></Protect.Any>,
      },
      {
        path: 'contracts/sign/:signToken',
        element: <Protect.Any><ContractSignPage /></Protect.Any>,
      },
      {
        path: '/',
        element: <AppContainer />,
        children: [
          {
            path: 'client-pulse',
            element: (
              <StaffOnly>
                <DailyPulseProvider>
                  <DailyPulse />
                </DailyPulseProvider>
              </StaffOnly>
            ),
          },
          {
            path: 'data-health',
            element: (
              <StaffOnly>
                <DataHealthPage />
              </StaffOnly>
            ),
          },
          {
            path: 'data-backfill',
            element: (
              <StaffOnly>
                <DataBackfillPage />
              </StaffOnly>
            ),
          },
          {
            path: 'performance-trends',
            element: <PerformanceTrends />,
          },
          {
            path: 'dashboard-overview',
            element: (
              <StaffOnly>
                <OverviewProvider>
                  <DashboardOverviewPage />
                </OverviewProvider>
              </StaffOnly>
            ), // ✅ NEW
          },
          
          {
            path: 'client-performance',
            element: (
              <StaffOnly>
                <ClientPerformanceProvider>
                  <ClientPerformance />
                </ClientPerformanceProvider>
              </StaffOnly>
            ),
          },
          {
            path: 'company-performance',
            element: (
              <StaffOnly>
                <CompanyPerformance />
              </StaffOnly>
            ),
          },
          {
            path: 'gender-performance',
            element: (
              <StaffOnly>
                <GenderPerformancePage />
              </StaffOnly>
            ),
          },
          {
            path: 'creative-performance',
            element: (
              <StaffOnly>
                <ClientPerformanceAggregator />
              </StaffOnly>
            ),
          },
          {
            path: 'designer-creative-performance',
            element: (
              <StaffOnly>
                <DesignerCreativePerformance />
              </StaffOnly>
            ),
          },
          {
            path: 'ad-performance',
            element: (
              <StaffOrAdPerformanceClient>
                <AdPerformancePage />
              </StaffOrAdPerformanceClient>
            ),
          },
          {
            path: 'client-performance-aggregator',
            element: <Navigate to={paths.dashboard.clientPerformanceAggregator} replace />,
          },

          {
            path: 'clients',
            element: (
              <ErrorBoundary>
                <ClientListPage />
              </ErrorBoundary>
            ),
          },
          {
            path: 'clients/invite-members',
            element: <ClientMemberInvitePage />,
          },
          {
            path: 'team',
            element: <TeamManagementPage />,
          },
          {
            path: 'clients/new',
            element: <StaffOnly><ClientCreatePage /></StaffOnly>,
          },
          {
            path: 'clients/:clientId/edit',
            element: <ClientHubPage />,
          },
          {
            path: 'clients/:clientId',
            element: <ClientHubPage />,
          },
          {
            path: 'clients/:clientId/hub',
            element: <ClientHubPage />,
          },
          {
            path: 'clients/:clientId/dashboard',
            element: <ClientDashboardPage />,
          },
          {
            path: 'clients/:clientSlug/schedule',
            element: <ClientSchedulePage />,
          },
          {
            path: 'clients/lanes',
            element: <ClientLanesPage />,
          },
          {
            path: 'prospects',
            element: <StaffOnly><ProspectListPage /></StaffOnly>,
          },
          {
            path: 'prospects/:token/fit-report',
            element: <StaffOnly><ProspectFitReportPage /></StaffOnly>,
          },
          {
            path: 'clients/onboarding',
            element: <OnboardingDashboardPage />,
          },
          {
            path: 'clients/onboarding/:clientSlug/workflow',
            element: <OnboardingChecklistPage />,
          },
          {
            path: 'clients/:clientSlug/webhook-setup',
            element: <WebhookSetupPage />,
          },
          {
            path: 'lead-delivery',
            element: <WebhookSetupPage />,
          },
          {
            path: 'email-notifications',
            element: <FiveStarEmailNotificationsPage />,
          },
          {
            path: 'clients/client-zips',
            element: <ClientZipsPage />
          },
          {
            path: 'zip-coverage',
            element: <ZipCoveragePage />,
          },
          {
            path: 'zip-targeting-analyzer',
            element: <StaffOnly><ZipTargetingAnalyzerPage /></StaffOnly>,
          },
          {
            path: 'data-integrations',
            element: <DataIntegrationHealthPage />,
          },
          {
            path: 'data-integrations/setup',
            element: <DataIntegrationWizardPage />,
          },
          {
            path: 'data-integrations/history',
            element: <DataIntegrationHistoryPage />,
          },
          {
            path: 'clients/zip-submissions',
            element: <ZipSubmissionsPage />,
          },
          {
            path: 'client-budgets',
            element: <ClientBudgetsPage />,
          },
          {
            path: 'budget-center',
            element: <BudgetCenterPage />,
          },
          {
            path: 'pacing-control',
            element: <PacingControlPage />,
          },
          {
            // Convenience: a client-context URL that redirects via query param
            // so deep links from sidebar work even before global picker loads.
            path: 'clients/:clientId/pacing-control',
            element: <PacingControlPage />,
          },
          {
            // Staff oversight: all currently-active holds across clients.
            path: 'active-pacing-holds',
            element: <ActiveHoldsAdminPage />,
          },
          {
            path: 'clients/:clientId/billing',
            element: <ClientBillingThresholdPage />,
          },
          {
            path: 'billing-thresholds',
            element: <BillingFleetPage />,
          },
          {
            path: 'billing-thresholds/activity',
            element: <BillingActivityPage />,
          },
          {
            // Staff meetings hub: open requests across clients + bypass.
            path: 'meeting-requests',
            element: <StaffMeetingRequestsPage />,
          },
          {
            path: 'budget-submissions',
            element: <BudgetSubmissionsPage />,
          },
          {
            path: 'client-offers',
            element: <ClientOffersPage />,
          },
          {
            path: 'clients/creative-knowledge',
            element: <ClientCreativeKnowledgePage />,
          },
          {
            path: 'creative-tasks',
            element: <CreativeTasksPage />,
          },
          {
            path: 'client-landing-pages',
            element: <ClientLandingPagesPage />,
          },
          {
            path: 'clients/client-landing-pages',
            element: <ClientLandingPagesPage />,
          },
          {
            path: 'my-invoices',
            element: <ClientInvoiceListPage />,
          },
          {
            path: 'my-invoices/:number',
            element: <ClientInvoiceDetailPage />,
          },
          {
            path: 'my-billing',
            element: <MyBillingPage />,
          },
          {
            path: 'my-billing/activity',
            element: <MyBillingActivityPage />,
          },
          {
            path: 'my-billing/statements',
            element: <MyStatementsPage />,
          },
          {
            path: 'dashboard/invoices',
            element: <InvoiceListPage />,
          },
          {
            path: 'dashboard/invoices/create',
            element: <InvoiceFormPage />,
          },
          {
            path: 'dashboard/invoices/settings',
            element: <InvoiceSettingsPage />,
          },
          {
            path: 'dashboard/invoices/:invoiceId',
            element: <InvoiceDetailPage />,
          },
          {
            path: 'dashboard/invoices/:invoiceId/edit',
            element: <InvoiceFormPage />,
          },
          {
            path: 'leads',
            element: <LeadListPage />,
          },
          {
            path: 'backend-data',
            element: <BackendDataListPage />,  // ✅ Route to your new page
          },

          {
            path: 'leads/:leadId',
            element: <LeadDetailPage />, 
          },
          {
            path: 'tasks',
            element: <TaskBoardPage />,
          },
          {
            path: 'settings/account',
            element: <AccountPage />,
          },
          {
            path: 'settings/api-access',
            element: <APIAccessPage />,
          },
          {
            path: 'dashboard/settings/account',
            element: <Navigate to="/settings/account" replace />,
          },
          {
            path: 'app1/tasklogger',
            element: <TaskLoggerPage />,
          },
          {
            path: 'ad-builder',
            element: <AdNameBuilderPage />,
          },
          {
            path: 'facebook/campaign-builder',
            element: <CampaignBuilderPage />,
          },
          {
            path: 'facebook/budget-optimizer',
            element: <BudgetOptimizerPage />,
          },
          {
            path: 'flows',
            element: <FlowHubPage />,
          },
          {
            path: 'flows/zip-code-updates',
            element: <ZipCodeUpdatesFlowPage />,
          },
          {
            path: 'client-inbox',
            element: <ClientInboxPage />,
          },
          {
            path: 'guru',
            element: <AgentChatPage />,
          },
          {
            path: 'agent-monitor',
            element: (
              <StaffOnly>
                <AgentMonitorPage />
              </StaffOnly>
            ),
          },
          {
            path: 'notifications',
            element: (
              <StaffOnly>
                <NotificationsPage />
              </StaffOnly>
            ),
          },
          {
            path: 'knowledge-base',
            element: <KnowledgeBasePage />,
          },
          {
            path: 'creative-studio',
            element: (
              <StaffOnly>
                <CreativeStudioPage />
              </StaffOnly>
            ),
          },
          {
            path: 'concept-system',
            element: (
              <StaffOnly>
                <ConceptSystemPage />
              </StaffOnly>
            ),
          },
          {
            path: 'client-backend-summary',
            element: <BackendDataSummaryPage />,
          },
          {
            path: 'email-mappings',
            element: <EmailMappingBuilderPage />,
          },

          
          
        ],
      },
      {
        path: 'invoices/:number',
        element: <PublicInvoicePage />,
      },
      {
        path: 'onboarding/parent/:parentSlug',
        element: <Protect.Authenticated><ParentOnboardingWizardPage /></Protect.Authenticated>,
      },
      {
        path: 'onboarding/five-star/:clientSlug',
        element: <Protect.Authenticated><FiveStarOnboardingWizardPage /></Protect.Authenticated>,
      },
      {
        path: 'onboarding/:clientSlug?',
        element: <Protect.Authenticated><OnboardingWizardPage /></Protect.Authenticated>,
      },
      {
        path: 'projections/:clientSlug?',
        element: <Protect.Authenticated><PublicProjectionsPage /></Protect.Authenticated>,
      },
      {
        path: '404',
        element: <Protect.Any><Page404 /></Protect.Any>,
      },
      {
        path: '500',
        element: <Protect.Any><Page500 /></Protect.Any>,
      },
      {
        path: '*',
        element: <Protect.Any><Page404 /></Protect.Any>,
      },
    ],
  },

]);

export default router;
